// Intake Questionnaire — read-only card on the MatterDetail Attorney tab
// (2026-08-29).  Renders ONLY when the matter has at least one questionnaire
// that followed the lead through conversion (lead_questionnaires.matter_id).
//
// Reads GET /api/matters/:id/questionnaire — gated by assertMatterAccess
// (Ship 1 deny-wins, uniform 404).  A 404 / 403 / empty list all render
// nothing: there is no "you can't see this" hint, matching the rest of the
// matter surface.  Responses render from the questionnaire's own snapshot via
// the shared QuestionnaireResponses renderer (escaped text, never HTML).

import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { getMatterQuestionnaire } from '../services/api'
import { QuestionnaireResponses, QNR_STATUS_CHIPS } from './LeadQuestionnairePanel'

const fmtTs = (ts) => (ts ? format(new Date(ts), 'MMM d, yyyy h:mm a') : '')

export default function MatterQuestionnaireCard ({ matterId }) {
  const [rows, setRows] = useState(null)   // null = not loaded / not visible
  const [open, setOpen] = useState(true)
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    if (!matterId) return
    let cancelled = false
    getMatterQuestionnaire(matterId)
      .then(r => { if (!cancelled) setRows(r.data?.questionnaires || []) })
      .catch(() => { if (!cancelled) setRows([]) })
    return () => { cancelled = true }
  }, [matterId])

  if (!rows || rows.length === 0) return null

  const submitted = rows.filter(q => q.status === 'submitted')
  const others = rows.filter(q => q.status !== 'submitted')

  return (
    <div id="questionnaire" className="mb-3">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 bg-white rounded-xl shadow-sm border-l-4 border-teal-500 hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3">
          <span className={`text-gray-400 text-xs transition-transform ${open ? 'rotate-90' : ''}`}>&#9654;</span>
          <span className="font-semibold text-gray-800">📝 Intake Questionnaire</span>
          <span className="text-xs text-gray-500">
            {submitted.length} submitted{others.length ? ` · ${others.length} other` : ''} · read-only, from the lead
          </span>
        </div>
      </button>
      {open && (
        <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 overflow-hidden p-4 space-y-3">
          {rows.map(q => {
            const st = q.effective_status || q.status
            const chip = QNR_STATUS_CHIPS[st] || { label: st, className: 'bg-gray-100 text-gray-700' }
            const isSub = q.status === 'submitted'
            const isOpen = expanded[q.id] ?? (isSub && submitted.length === 1)
            return (
              <div key={q.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${chip.className}`}>{chip.label}</span>
                  <span className="text-gray-800 font-medium truncate flex-1">{q.template_name || 'Questionnaire'}</span>
                  {isSub && (
                    <button onClick={() => setExpanded(x => ({ ...x, [q.id]: !isOpen }))} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                      {isOpen ? 'Hide responses' : 'View responses'}
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Sent {fmtTs(q.sent_at)}{q.sent_by_name ? ` by ${q.sent_by_name}` : ''} to {q.sent_to_email}
                  {q.submitted_at ? <span className="text-emerald-700"> · submitted {fmtTs(q.submitted_at)}</span> : null}
                  {q.voided_at ? <span className="text-red-700"> · voided {fmtTs(q.voided_at)}{q.void_reason ? ` (${q.void_reason})` : ''}</span> : null}
                  {q.supersedes_id ? ` · reopened from #${q.supersedes_id}` : ''}
                </div>
                {isSub && isOpen && (
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <QuestionnaireResponses questionnaire={q} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
